import type { BlockNoteEditor, PartialBlock } from '@blocknote/core';
import * as React from 'react';

import {
  findDeferredBlockInsertAfterId,
  isDeferredCustomBlockType,
} from '../deferred-custom-blocks';

interface DeferredBlockInsert {
  block: PartialBlock;
  insertAfterId: string | null;
}

interface UseDropdownBlockRestorationParams {
  editor: BlockNoteEditor;
  initialContent?: PartialBlock[];
  isRestoringDropdownsRef: React.MutableRefObject<boolean>;
  /**
   * Whether the editor mounts as editable. Editable mounts also defer (and
   * therefore restore) horizontalRule blocks; read-only mounts never restore
   * them because their node views crash during read-only initialization.
   */
  editable?: boolean;
  /**
   * Fired exactly once per content load, after the initial content has settled:
   * immediately when there are no deferred blocks to restore, or after deferred
   * custom blocks (dropdown/date/horizontalRule) have been re-inserted. Deferred
   * blocks are stripped before mount, so the document is momentarily incomplete;
   * consumers use this to act on the settled document (e.g. re-baseline
   * unsaved-changes detection) instead of racing the restoration timer. Receives
   * the editor's settled document.
   */
  onSettled?: (blocks: PartialBlock[]) => void;
}

function hasDeferredCustomBlocks(
  blocks: PartialBlock[],
  editable: boolean,
): boolean {
  return blocks.some((block) => isDeferredCustomBlockType(block.type, editable));
}

/**
 * Prepares a deferred block for `insertBlocks`. Persisted horizontalRule
 * blocks can carry `content: []` / `children: []`, which BlockNote rejects for
 * `content: 'none'` block specs, so those fields are stripped before insert.
 */
function sanitizeDeferredBlock(block: PartialBlock): PartialBlock {
  if ((block as { type?: string }).type !== 'horizontalRule') {
    return block;
  }

  // Derive from the existing block (already a valid PartialBlock) and drop only
  // the two offending fields, preserving id, type, and any props without
  // reconstructing — and so without casting away the block's type.
  const sanitized = { ...block };
  delete sanitized.content;
  delete sanitized.children;

  return sanitized;
}

function collectDeferredBlocks(
  blocks: PartialBlock[],
  editable: boolean,
): DeferredBlockInsert[] {
  return blocks.flatMap((block, index) => {
    if (!isDeferredCustomBlockType(block.type, editable) || !block.id) {
      return [];
    }

    return [
      {
        block: sanitizeDeferredBlock(block),
        insertAfterId: findDeferredBlockInsertAfterId(blocks, index, editable),
      },
    ];
  });
}

/**
 * Restores deferred custom blocks (dropdown/date/horizontalRule) after BlockNote
 * has mounted the base document, preserving their original relative positions by
 * block id. These blocks are stripped from `initialContent` before mount because
 * their node views can throw during BlockNote's initial parse; this hook
 * re-inserts them once the editor is ready.
 *
 * It also emits a single {@link UseDropdownBlockRestorationParams.onSettled}
 * signal per content load once the initial content has fully settled — fired
 * immediately when there is nothing to restore, or after restoration otherwise.
 * Consumers use this (rather than a timer) to know the document is complete,
 * e.g. to re-baseline unsaved-changes detection against the editor's own
 * representation.
 */
export function useDropdownBlockRestoration({
  editor,
  initialContent,
  isRestoringDropdownsRef,
  editable = false,
  onSettled,
}: UseDropdownBlockRestorationParams): void {
  const originalContentRef = React.useRef(initialContent);
  const hasRestoredDropdownsRef = React.useRef(false);
  const hasSettledRef = React.useRef(false);

  React.useEffect(() => {
    originalContentRef.current = initialContent;
    hasRestoredDropdownsRef.current = false;
    hasSettledRef.current = false;
  }, [initialContent]);

  React.useEffect(() => {
    if (!originalContentRef.current) {
      return undefined;
    }

    // No deferred blocks to restore right now: the content is already settled, so
    // signal settle once per content load. Do NOT latch the restoration guard —
    // `hasDeferredCustomBlocks` is editable-dependent (horizontalRule defers only
    // when editable), so an in-place `editable` toggle can newly defer blocks that
    // must still be restored on a later re-run of this effect.
    if (!hasDeferredCustomBlocks(originalContentRef.current, editable)) {
      if (!hasSettledRef.current) {
        hasSettledRef.current = true;
        onSettled?.(editor.document as PartialBlock[]);
      }
      return undefined;
    }

    // There ARE deferred blocks: restore them at most once per content load.
    if (hasRestoredDropdownsRef.current) {
      return undefined;
    }

    const restoreCustomBlocks = () => {
      isRestoringDropdownsRef.current = true;

      try {
        const currentBlocks = editor.document;
        const originalBlocks = originalContentRef.current;

        if (!originalBlocks) {
          isRestoringDropdownsRef.current = false;
          return;
        }

        const currentBlockMap = new Map(currentBlocks.map((block) => [block.id, block]));
        const blocksToInsert = collectDeferredBlocks(originalBlocks, editable);

        // Insert from the end so earlier insertions do not shift later targets.
        // Each insert is isolated so one failing block cannot abort the rest
        // of the restoration.
        blocksToInsert.reverse().forEach(({ block, insertAfterId }) => {
          try {
            // The document may already hold this block. Discard replaces the
            // document in place with the saved content — deferred blocks
            // included — and then moves the editor seed, which changes
            // `initialContent`'s identity and re-arms this hook on a still-
            // mounted editor. Re-inserting would duplicate the block, so
            // restore only what is actually missing.
            if (block.id && currentBlockMap.has(block.id)) {
              return;
            }

            if (insertAfterId && currentBlockMap.has(insertAfterId)) {
              editor.insertBlocks([block], { id: insertAfterId }, 'after');
            } else if (currentBlocks.length > 0) {
              editor.insertBlocks([block], currentBlocks[0], 'before');
            }
          } catch (insertError) {
            console.warn(
              `Failed to restore deferred block "${block.id}" (${String(
                (block as { type?: string }).type,
              )}):`,
              insertError,
            );
          }
        });

        if (blocksToInsert.length > 0 && currentBlocks.length === 0) {
          editor.replaceBlocks(editor.document, originalBlocks);
        }

        hasRestoredDropdownsRef.current = true;
      } catch (error) {
        console.warn('Failed to restore custom blocks:', error);

        try {
          if (originalContentRef.current) {
            editor.replaceBlocks(editor.document, originalContentRef.current);
            hasRestoredDropdownsRef.current = true;
          }
        } catch (fallbackError) {
          console.warn('Fallback restoration also failed:', fallbackError);
        }
      } finally {
        // Reset the suppression flag before notifying so an onSettled handler
        // that reads the editor isn't skipped by the restoration guard.
        isRestoringDropdownsRef.current = false;
        if (!hasSettledRef.current) {
          hasSettledRef.current = true;
          onSettled?.(editor.document as PartialBlock[]);
        }
      }
    };

    const checkReady = () => {
      try {
        if (editor.document !== undefined) {
          restoreCustomBlocks();
          return;
        }

        setTimeout(checkReady, 50);
      } catch {
        setTimeout(checkReady, 50);
      }
    };

    // Custom blocks are restored only after BlockNote finishes creating node
    // views; restoring them through initialContent can trigger position errors.
    const timer = setTimeout(checkReady, 100);

    return () => clearTimeout(timer);
    // `initialContent` is included so restoration re-runs when content arrives
    // or is replaced after the editor mounts (the ref-reset effect above runs
    // first in the same commit and re-arms `hasRestoredDropdownsRef`).
  }, [editable, editor, initialContent, isRestoringDropdownsRef, onSettled]);
}
